/**
 * NodeInspectorPanel — right-side panel showing concept details when a node is selected.
 */
import { X, ArrowRight, ArrowLeft, ExternalLink } from 'lucide-react';
import { clsx } from 'clsx';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useOntologyStore } from '@/stores/ontology';
import { ENTITY_TYPE_COLORS } from './OntologyGraph';
import type { OntologyNodeData, OntologyEdgeData } from '@/services/api';

interface Props {
  nodeId: string;
  onClose: () => void;
  onNavigate?: (nodeId: string) => void;
}

function EntityTypeBadge({ type }: { type: string }) {
  const color = ENTITY_TYPE_COLORS[type] ?? '#94a3b8';
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-mono font-bold uppercase tracking-wider"
      style={{ backgroundColor: `${color}22`, color, border: `1px solid ${color}44` }}
    >
      {type}
    </span>
  );
}

function RelTypeBadge({ type, color }: { type: string; color: string }) {
  return (
    <span
      className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-mono font-bold uppercase"
      style={{ backgroundColor: `${color}22`, color, border: `1px solid ${color}44` }}
    >
      {type.replace(/_/g, ' ')}
    </span>
  );
}

const REL_COLORS: Record<string, string> = {
  DRIVES:            '#3b82f6',
  INHIBITS:          '#ef4444',
  RESOLVES_CONCERN:  '#22c55e',
  REMOVES_BARRIER:   '#f97316',
  CONFIRMS_IDENTITY: '#94a3b8',
  SATISFIES:         '#a78bfa',
  CO_OCCURS:         '#475569',
};

export function NodeInspectorPanel({ nodeId, onClose, onNavigate }: Props) {
  const graph = useOntologyStore((s) => s.currentGraph());

  if (!graph) return null;

  const node = graph.nodes.find((n) => n.id === nodeId);
  if (!node) return null;

  const outgoing = graph.edges.filter((e) => e.source === nodeId);
  const incoming = graph.edges.filter((e) => e.target === nodeId);

  const getNodeName = (id: string) =>
    graph.nodes.find((n) => n.id === id)?.name ?? id.replace(/_/g, ' ');

  return (
    <div className="flex flex-col h-full bg-navy-900 border-l border-navy-700 w-80 flex-shrink-0 overflow-hidden">
      {/* Header */}
      <div className="flex items-start justify-between p-4 border-b border-navy-700">
        <div className="min-w-0 flex-1 pr-3">
          <div className="flex items-center gap-2 mb-1.5">
            <div
              className="w-3 h-3 rounded-full flex-shrink-0"
              style={{ backgroundColor: ENTITY_TYPE_COLORS[node.entity_type] ?? '#94a3b8' }}
            />
            <EntityTypeBadge type={node.entity_type} />
          </div>
          <h3 className="text-white font-semibold text-sm leading-tight">{node.name}</h3>
          <p className="text-slate-500 text-xs mt-0.5 font-mono">{node.id}</p>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 rounded-lg hover:bg-navy-700 text-slate-500 hover:text-white transition-colors flex-shrink-0"
        >
          <X size={14} />
        </button>
      </div>

      {/* Metadata */}
      <div className="px-4 py-3 border-b border-navy-800 space-y-2">
        <MetaRow label="Category" value={node.category.replace(/_/g, ' ')} />
        <MetaRow label="Status" value={node.status} />
        {node.causal_strength > 0 && (
          <MetaRow label="Causal Strength" value={node.causal_strength.toFixed(2)} />
        )}
        {node.causal_stability > 0 && (
          <MetaRow label="Causal Stability" value={node.causal_stability.toFixed(2)} />
        )}
        {node.cognitive_mechanism && (
          <MetaRow label="Mechanism" value={node.cognitive_mechanism} />
        )}
        {node.version_introduced && (
          <MetaRow label="Introduced" value={node.version_introduced} />
        )}
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto">
        {/* Markdown body */}
        {node.body && (
          <div className="px-4 py-3 border-b border-navy-800">
            <p className="text-[10px] font-mono uppercase text-slate-600 font-bold mb-2 tracking-wider">Description</p>
            <div className="prose prose-sm prose-invert prose-p:text-slate-300 prose-p:text-xs prose-headings:text-slate-200 prose-headings:text-xs prose-li:text-xs prose-li:text-slate-300 max-w-none">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {node.body}
              </ReactMarkdown>
            </div>
          </div>
        )}

        {/* Persona activations */}
        {Object.keys(node.persona_activations).length > 0 && (
          <div className="px-4 py-3 border-b border-navy-800">
            <p className="text-[10px] font-mono uppercase text-slate-600 font-bold mb-2 tracking-wider">
              Persona Activations
            </p>
            <div className="space-y-1.5">
              {Object.entries(node.persona_activations)
                .sort(([, a], [, b]) => (b as number) - (a as number))
                .map(([persona, score]) => (
                  <div key={persona} className="flex items-center justify-between">
                    <span className="text-xs text-slate-400 truncate">
                      {(persona as string).replace(/_/g, ' ')}
                    </span>
                    <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                      <div className="w-16 h-1 bg-navy-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-brand-teal rounded-full"
                          style={{ width: `${Math.min(100, (score as number) * 100)}%` }}
                        />
                      </div>
                      <span className="text-[10px] text-slate-500 w-8 text-right">
                        {typeof score === 'number' ? score.toFixed(2) : score}
                      </span>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Outgoing edges */}
        <EdgeList
          title="Drives / Connects To"
          edges={outgoing}
          direction="out"
          getNodeName={getNodeName}
          onNavigate={onNavigate}
        />

        {/* Incoming edges */}
        <EdgeList
          title="Influenced By"
          edges={incoming}
          direction="in"
          getNodeName={getNodeName}
          onNavigate={onNavigate}
        />
      </div>
    </div>
  );
}

function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start gap-2">
      <span className="text-[10px] font-mono text-slate-600 uppercase tracking-wide w-24 flex-shrink-0 pt-0.5">
        {label}
      </span>
      <span className="text-xs text-slate-300 leading-tight">{value}</span>
    </div>
  );
}

function EdgeList({
  title,
  edges,
  direction,
  getNodeName,
  onNavigate,
}: {
  title: string;
  edges: OntologyEdgeData[];
  direction: 'in' | 'out';
  getNodeName: (id: string) => string;
  onNavigate?: (nodeId: string) => void;
}) {
  if (edges.length === 0) return null;
  return (
    <div className="px-4 py-3 border-b border-navy-800 last:border-b-0">
      <p className="text-[10px] font-mono uppercase text-slate-600 font-bold mb-2 tracking-wider">
        {title} ({edges.length})
      </p>
      <div className="space-y-1.5">
        {edges.slice(0, 20).map((edge) => {
          const neighborId = direction === 'out' ? edge.target : edge.source;
          const color = REL_COLORS[edge.rel_type] ?? '#64748b';
          return (
            <div key={edge.id} className="flex items-center gap-1.5 group">
              {direction === 'in' && <ArrowRight size={10} className="text-slate-600 flex-shrink-0" />}
              <RelTypeBadge type={edge.rel_type} color={color} />
              <button
                onClick={() => onNavigate?.(neighborId)}
                className="text-xs text-slate-400 hover:text-brand-teal truncate transition-colors"
                title={neighborId}
              >
                {getNodeName(neighborId)}
              </button>
              {direction === 'out' && <ArrowRight size={10} className="text-slate-600 flex-shrink-0" />}
            </div>
          );
        })}
        {edges.length > 20 && (
          <p className="text-[10px] text-slate-600">+{edges.length - 20} more</p>
        )}
      </div>
    </div>
  );
}
